import { ReactNode, use, useEffect } from 'react';
import { hideBootLoader, isBootLoaderShowing } from '../../util/bootLoader';
import { Loader, LoaderProps } from './Loader';

/**
 * The standard Loader, except nothing while index.html's loader still covers the page. Read at render
 * time, not subscribed: the loader only ever goes when another screen mounts, which re-renders anyway.
 */
export function BootAwareLoader(props: LoaderProps) {
  return isBootLoaderShowing() ? null : <Loader {...props} />;
}

// How long the shell holds for a slot at boot before it shows without it. A bound only: the slot's data is
// primed before the shell renders and lands well within it.
const SHELL_HOLD_MS = 3000;
let shellHoldMs = SHELL_HOLD_MS;
let shellHold: Promise<void> | undefined;

function holdShell(): Promise<void> {
  return (shellHold ??= new Promise((resolve) => setTimeout(resolve, shellHoldMs)));
}

/**
 * A shell slot's Suspense fallback, except while index.html's loader still covers the page: then it
 * suspends itself. The shell renders in the boot's transition (see AppLoader), where that makes the boundary
 * above the shell wait as well, without committing a fallback: the slot's data holds the whole shell, the
 * shell appears in one piece, and nothing is throttled. Outside a transition the boundary above would commit
 * its fallback with only this wait to retry on, so the wait is bounded. Past the bound, or once the shell is
 * on, it is the slot's ordinary fallback: `children`, nothing by default.
 */
export function BootAwareFallback({ children = null }: { children?: ReactNode }) {
  if (isBootLoaderShowing()) {
    use(holdShell());
  }
  return children;
}

export function __TEST_ONLY__resetShellHold(boundMs = SHELL_HOLD_MS) {
  shellHold = undefined;
  shellHoldMs = boundMs;
}

/** Removes index.html's loader once the calling screen is on. */
export function useHideBootLoader() {
  useEffect(() => {
    hideBootLoader();
  }, []);
}

/** For a branch that shows content of its own before the shell: mount this beside it. */
export function HideBootLoader() {
  useHideBootLoader();
  return null;
}
